/* Legacy campaign page: variant selection, gallery, set and sticky add to cart. */
(() => {
  'use strict';

  const root = document.querySelector('[data-lc-root]');
  if (!root) return;

  const configEl = root.querySelector('[data-lc-config]');
  if (!configEl) return;

  const config = JSON.parse(configEl.textContent);
  const strings = config.strings;
  const themeSettings = (window.themeVariables && window.themeVariables.settings) || {};
  const currencyCodeEnabled = typeof themeSettings.currencyCodeEnabled === 'boolean'
    ? themeSettings.currencyCodeEnabled
    : !!config.currency_code_enabled;
  const moneyFormat = currencyCodeEnabled
    ? (themeSettings.moneyWithCurrencyFormat || config.money_with_currency_format)
    : (themeSettings.moneyFormat || config.money_format);
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Helpers ---------- */

  const shortMoneyFormat = themeSettings.moneyFormat || config.money_format;

  function formatMoney(cents, format = moneyFormat) {
    format = String(format || '${{amount}}').replace(/<[^>]*>/g, '');
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    const withDelimiters = (value, precision, thousands = ',', decimal = '.') => {
      const fixed = (value / 100).toFixed(precision);
      const parts = fixed.split('.');
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${whole}${decimal}${parts[1]}` : whole;
    };

    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        amount = withDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        amount = withDelimiters(cents, 2, ' ', '.');
        break;
      default:
        amount = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, amount);
  }

  /* Button labels show the currency sign only, without the currency code. */
  const buttonMoney = (cents) => formatMoney(cents, shortMoneyFormat);

  const fill = (template, key, value) => String(template).replace(`[${key}]`, value);

  function debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }

  const discountTiers = config.discount_tiers || { 3: config.discount_percent };
  const minPieces = config.min_pieces || 2;

  function tierPercent(count) {
    return Number(discountTiers[count] || 0);
  }

  function discounted(cents, percent = config.discount_percent) {
    return Math.round((cents * (100 - percent)) / 100);
  }

  function showStatus(el, message) {
    if (!el) return;
    el.textContent = '';
    if (!message) return;
    el.append(message, ' ');
    const link = document.createElement('a');
    link.className = 'link';
    link.href = config.cart_url;
    link.textContent = strings.view_cart;
    el.append(link);
  }

  function showError(el, message) {
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  /* Adds items with the Cart AJAX API and lets the theme refresh and open its cart drawer. */
  async function addToCart(items, { button, statusEl, errorEl, successMessage }) {
    if (button) {
      if (button.getAttribute('aria-busy') === 'true') return false;
      button.setAttribute('aria-busy', 'true');
    }
    showError(errorEl, '');
    showStatus(statusEl, '');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    try {
      const response = await fetch(`${config.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname
        })
      });
      const json = await response.json();

      if (!response.ok) {
        showError(errorEl, json.description || json.message || strings.error);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return false;
      }

      if (themeSettings.cartType === 'page') {
        window.location.href = config.cart_url;
        return true;
      }

      const cart = await (await fetch(`${config.cart_url}.js`)).json();
      cart.sections = json.sections;
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        })
      );
      showStatus(statusEl, successMessage);
      return true;
    } catch (error) {
      showError(errorEl, strings.error);
      return false;
    } finally {
      if (button) button.removeAttribute('aria-busy');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  const emitChange = (detail) => root.dispatchEvent(new CustomEvent('lc:change', { detail }));

  /* ---------- Product block ---------- */

  class ProductBlock {
    constructor(el) {
      this.el = el;
      this.data = JSON.parse(el.querySelector('[data-lc-product-json]').textContent);
      this.key = this.data.key;
      this.selected = this.data.initial.slice();
      this.form = el.querySelector('[data-lc-form]');
      this.idInput = el.querySelector('[data-lc-variant-id]');
      this.button = el.querySelector('[data-lc-add]');
      this.buttonLabel = el.querySelector('[data-lc-add-label]');
      this.priceEl = el.querySelector('[data-lc-price]');
      this.errorEl = el.querySelector('[data-lc-error]');
      this.statusEl = el.querySelector('[data-lc-status]');
      this.track = el.querySelector('[data-lc-track]');
      this.slides = Array.from(el.querySelectorAll('[data-lc-slide]'));
      this.thumbs = Array.from(el.querySelectorAll('[data-lc-thumb]'));
      this.currentMetal = null;

      this.form.noValidate = true;
      this.form.addEventListener('change', (event) => {
        const input = event.target.closest('input[data-lc-option]');
        if (input) this.setOption(Number(input.dataset.lcOption), input.value);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.add();
      });

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.lcThumb)));
      });
      if (this.track) {
        this.track.addEventListener('scroll', debounce(() => this.syncThumbs(), 80), { passive: true });
      }

      this.setupVideos();
      this.update();
    }

    setupVideos() {
      this.videos = Array.from(this.el.querySelectorAll('[data-lc-slide] video'));
      if (!this.videos.length) return;

      this.videos.forEach((video) => {
        video.muted = true;
        video.setAttribute('playsinline', '');
        const toggle = video.parentElement.querySelector('[data-lc-video-toggle]');
        const sync = () => {
          if (!toggle) return;
          const paused = video.paused;
          toggle.classList.toggle('is-paused', paused);
          toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
          toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
        };
        video.addEventListener('play', sync);
        video.addEventListener('pause', sync);
        sync();
        if (toggle) {
          toggle.addEventListener('click', () => {
            if (video.paused) {
              delete video.dataset.userPaused;
              video.play().catch(() => {});
            } else {
              video.dataset.userPaused = 'true';
              video.pause();
            }
          });
        }
      });

      if (!('IntersectionObserver' in window)) return;
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const slide = video.closest('[data-lc-slide]');
            const shouldPlay =
              entry.isIntersecting &&
              entry.intersectionRatio >= 0.6 &&
              slide &&
              !slide.hidden &&
              !video.dataset.userPaused &&
              !reducedMotion;
            if (shouldPlay) video.play().catch(() => {});
            else if (!video.paused) video.pause();
          });
        },
        { threshold: [0, 0.6, 1] }
      );
      this.videos.forEach((video) => this.videoObserver.observe(video));
    }

    pauseHiddenVideos() {
      (this.videos || []).forEach((video) => {
        const slide = video.closest('[data-lc-slide]');
        if (slide && slide.hidden && !video.paused) video.pause();
      });
    }

    get variant() {
      if (this.selected.some((value) => value === null)) return null;
      return this.data.variants.find((v) => v.options.every((option, i) => option === this.selected[i])) || null;
    }

    get metalIndex() {
      return Math.max(0, this.data.metal_values.indexOf(this.selected[0]));
    }

    get metalKey() {
      return this.metalIndex === 1 ? 'white' : 'gold';
    }

    get missingIndex() {
      return this.selected.findIndex((value) => value === null);
    }

    get displayPrice() {
      const variant = this.variant;
      if (variant) return variant.price;
      const prices = this.data.variants
        .filter((v) => v.options[0] === this.selected[0])
        .map((v) => v.price);
      return prices.length ? Math.min(...prices) : 0;
    }

    get thumbnail() {
      return this.data.thumbs[this.metalKey] || this.data.thumbs.gold || '';
    }

    summary() {
      return this.selected
        .map((value, index) => (value === null && index === this.data.choice_index ? strings.no_size : value))
        .filter(Boolean)
        .join(' · ');
    }

    setOption(index, value) {
      if (this.selected[index] === value) return;
      this.selected[index] = value;
      this.form.querySelectorAll(`input[data-lc-option="${index}"]`).forEach((input) => {
        input.checked = input.value === value;
      });
      showError(this.errorEl, '');
      this.update();
      emitChange({ key: this.key, index, value });
    }

    setMetalIndex(index) {
      const value = this.data.metal_values[index];
      if (value !== undefined) this.setOption(0, value);
    }

    update() {
      const variant = this.variant;

      this.el.querySelectorAll('[data-lc-option-label]').forEach((label) => {
        label.textContent = this.selected[Number(label.dataset.lcOptionLabel)] || '';
      });

      this.form.querySelectorAll('input[data-lc-option]').forEach((input) => {
        const index = Number(input.dataset.lcOption);
        if (index === 0) return;
        const available = this.data.variants.some(
          (v) => v.available && v.options[0] === this.selected[0] && v.options[index] === input.value
        );
        const label = this.form.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('is-disabled', !available);
      });

      this.idInput.value = variant ? variant.id : '';

      const price = formatMoney(this.displayPrice);
      if (this.priceEl) {
        this.priceEl.textContent = variant || !this.data.price_varies ? price : fill(strings.from, 'price', price);
      }

      if (!variant) {
        this.buttonLabel.textContent = this.data.choose_label;
        this.button.disabled = false;
      } else if (!variant.available) {
        this.buttonLabel.textContent = strings.sold_out;
        this.button.disabled = true;
      } else {
        this.buttonLabel.textContent = fill(strings.add_with_price, 'price', buttonMoney(this.displayPrice));
        this.button.disabled = false;
      }

      this.filterGallery();
    }

    filterGallery() {
      const metal = this.metalKey;
      if (this.currentMetal === metal) return;
      this.currentMetal = metal;

      const hasMetal = this.slides.some((slide) => slide.dataset.metal === metal);
      this.slides.forEach((slide) => {
        slide.hidden = hasMetal && slide.dataset.metal !== metal;
      });
      this.thumbs.forEach((thumb) => {
        thumb.hidden = hasMetal && thumb.dataset.metal !== metal;
      });
      if (this.track) this.track.scrollLeft = 0;
      this.pauseHiddenVideos();
      this.syncThumbs();
    }

    goTo(index) {
      const slide = this.slides[index];
      if (!slide || !this.track) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
      this.setCurrentThumb(index);
    }

    syncThumbs() {
      if (!this.track) return;
      const visible = this.slides.filter((slide) => !slide.hidden);
      if (!visible.length) return;
      const left = this.track.scrollLeft;
      const current = visible.reduce((best, slide) =>
        Math.abs(slide.offsetLeft - left) < Math.abs(best.offsetLeft - left) ? slide : best
      );
      this.setCurrentThumb(this.slides.indexOf(current));
    }

    setCurrentThumb(index) {
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', Number(thumb.dataset.lcThumb) === index ? 'true' : 'false');
      });
    }

    focusMissing() {
      const fieldset = this.form.querySelector(`[data-lc-fieldset="${this.missingIndex}"]`);
      if (!fieldset) return;
      fieldset.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      const first = fieldset.querySelector('input:not(:disabled)');
      if (first) first.focus({ preventScroll: true });
    }

    async add() {
      const variant = this.variant;
      if (!variant) {
        showError(this.errorEl, this.data.choose_error);
        this.focusMissing();
        return false;
      }
      if (!variant.available) return false;
      return addToCart([{ id: variant.id, quantity: 1 }], {
        button: this.button,
        statusEl: this.statusEl,
        errorEl: this.errorEl,
        successMessage: strings.added
      });
    }
  }

  const blocks = new Map();
  root.querySelectorAll('[data-lc-product]').forEach((el) => {
    const block = new ProductBlock(el);
    blocks.set(block.key, block);
  });

  const ring = blocks.get('ring');
  const ringChoiceIndex = ring ? ring.data.choice_index : -1;

  function setRingSize(value) {
    if (ring && ringChoiceIndex > -1) ring.setOption(ringChoiceIndex, value);
  }

  function syncSizeInputs(selector) {
    const current = ring && ringChoiceIndex > -1 ? ring.selected[ringChoiceIndex] : null;
    root.querySelectorAll(selector).forEach((input) => {
      input.checked = input.value === current;
      const label = root.querySelector(`label[for="${input.id}"]`);
      if (label && ring) {
        const available = ring.data.variants.some(
          (v) => v.available && v.options[0] === ring.selected[0] && v.options[ringChoiceIndex] === input.value
        );
        label.classList.toggle('is-disabled', !available);
      }
    });
  }

  /* ---------- Global metal ---------- */

  const metalInputs = Array.from(root.querySelectorAll('input[data-lc-global-metal]'));

  function showGlobalMetalImages(metalKey) {
    root.querySelectorAll('[data-lc-metal-image]').forEach((el) => {
      el.hidden = el.dataset.metal !== metalKey;
    });
  }

  metalInputs.forEach((input) => {
    input.addEventListener('change', () => {
      const index = Number(input.value);
      blocks.forEach((block) => block.setMetalIndex(index));
      showGlobalMetalImages(index === 1 ? 'white' : 'gold');
    });
  });

  function syncGlobalMetal() {
    const indexes = Array.from(blocks.values()).map((block) => block.metalIndex);
    const same = indexes.length && indexes.every((index) => index === indexes[0]);
    metalInputs.forEach((input) => {
      input.checked = same && Number(input.value) === indexes[0];
    });
    if (same) showGlobalMetalImages(indexes[0] === 1 ? 'white' : 'gold');
  }

  /* ---------- Set ---------- */

  const setEl = root.querySelector('[data-lc-set]');
  const setKeys = (config.set_keys || []).filter((key) => blocks.has(key));

  const set = setEl
    ? {
        form: setEl.querySelector('[data-lc-set-form]'),
        button: setEl.querySelector('[data-lc-set-add]'),
        buttonLabel: setEl.querySelector('[data-lc-set-add-label]'),
        errorEl: setEl.querySelector('[data-lc-set-error]'),
        statusEl: setEl.querySelector('[data-lc-set-status]'),
        totalEl: setEl.querySelector('[data-lc-set-total]'),
        regularEl: setEl.querySelector('[data-lc-set-regular]'),
        savingsEl: setEl.querySelector('[data-lc-set-savings]'),
        badgeEl: setEl.querySelector('[data-lc-set-badge]'),
        includeInputs: Array.from(setEl.querySelectorAll('input[data-lc-set-include]'))
      }
    : null;

  /* Pieces ticked in the set section. Without the checkboxes every piece counts. */
  function isIncluded(key) {
    if (!set) return true;
    const input = set.includeInputs.find((el) => el.dataset.lcSetInclude === key);
    return !input || input.checked;
  }

  function setState() {
    const all = setKeys.map((key) => blocks.get(key));
    const parts = all.filter((block) => isIncluded(block.key));
    const enough = parts.length >= minPieces;
    const percent = enough ? tierPercent(parts.length) : 0;
    let regular = 0;
    let total = 0;
    parts.forEach((block) => {
      regular += block.displayPrice;
      total += discounted(block.displayPrice, percent);
    });
    const complete = enough && parts.every((block) => block.variant);
    const available = complete && parts.every((block) => block.variant.available);
    return { all, parts, count: parts.length, enough, percent, regular, total, complete, available };
  }

  function updateSet() {
    if (!set) return;
    const state = setState();

    state.all.forEach((block) => {
      const item = setEl.querySelector(`[data-lc-set-item="${block.key}"]`);
      if (item) item.classList.toggle('is-excluded', !isIncluded(block.key));
      const price = setEl.querySelector(`[data-lc-set-item-price="${block.key}"]`);
      if (price) price.textContent = formatMoney(block.displayPrice);
      setEl.querySelectorAll(`[data-lc-set-image="${block.key}"]`).forEach((image) => {
        image.hidden = image.dataset.metal !== block.metalKey;
      });
      setEl.querySelectorAll(`[data-lc-set-label^="${block.key}-"]`).forEach((label) => {
        const index = Number(label.dataset.lcSetLabel.split('-').pop());
        label.textContent = block.selected[index] || '';
      });
      setEl.querySelectorAll(`input[data-lc-set-key="${block.key}"]`).forEach((input) => {
        const index = Number(input.dataset.lcSetOption);
        input.checked = block.selected[index] === input.value;
        if (index === 0) return;
        const available = block.data.variants.some(
          (v) => v.available && v.options[0] === block.selected[0] && v.options[index] === input.value
        );
        const label = setEl.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('is-disabled', !available);
      });
    });

    set.totalEl.textContent = formatMoney(state.total);
    set.regularEl.textContent = formatMoney(state.regular);
    set.regularEl.hidden = !state.percent;
    set.savingsEl.textContent = state.percent
      ? fill(strings.savings, 'amount', formatMoney(state.regular - state.total))
      : strings.set_min;
    if (set.badgeEl) {
      set.badgeEl.hidden = !state.percent;
      set.badgeEl.textContent = fill(strings.set_badge, 'percent', state.percent);
    }

    if (!state.enough) {
      set.buttonLabel.textContent = strings.set_min;
      set.button.disabled = true;
    } else if (!state.complete) {
      set.buttonLabel.textContent = strings.set_choose;
      set.button.disabled = false;
    } else if (!state.available) {
      set.buttonLabel.textContent = strings.sold_out;
      set.button.disabled = true;
    } else {
      set.buttonLabel.textContent = fill(strings.set_add, 'price', buttonMoney(state.total));
      set.button.disabled = false;
    }
  }

  async function addSet() {
    if (!set) return false;
    const state = setState();
    if (!state.enough) {
      showError(set.errorEl, strings.set_min);
      setEl.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      return false;
    }
    if (!state.complete) {
      showError(set.errorEl, strings.choose_size_error);
      const missing = state.parts.find((block) => !block.variant);
      const fieldset = missing && setEl.querySelector(`[data-lc-set-fieldset="${missing.key}-${missing.missingIndex}"]`);
      if (fieldset) {
        fieldset.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        const first = fieldset.querySelector('input');
        if (first) first.focus({ preventScroll: true });
      }
      return false;
    }
    if (!state.available) return false;
    showError(set.errorEl, '');
    return addToCart(
      state.parts.map((block) => ({ id: block.variant.id, quantity: 1 })),
      { button: set.button, statusEl: set.statusEl, errorEl: set.errorEl, successMessage: fill(strings.set_added, 'percent', state.percent) }
    );
  }

  if (set) {
    set.form.addEventListener('change', (event) => {
      if (event.target.matches('input[data-lc-set-include]')) {
        showError(set.errorEl, '');
        updateSet();
        updateDialog();
        renderSticky();
        return;
      }
      const input = event.target.closest('input[data-lc-set-option]');
      const block = input && blocks.get(input.dataset.lcSetKey);
      if (block) {
        showError(set.errorEl, '');
        /* Customising a piece that was left out adds it back to the set. */
        const include = set.includeInputs.find((el) => el.dataset.lcSetInclude === block.key);
        if (include && !include.checked) include.checked = true;
        block.setOption(Number(input.dataset.lcSetOption), input.value);
      }
    });
    set.form.addEventListener('submit', (event) => {
      event.preventDefault();
      addSet();
    });
  }

  /* ---------- Ring size dialog (used by the sticky bar) ---------- */

  const dialog = root.querySelector('[data-lc-size-dialog]');
  let dialogAction = 'ring';

  function updateDialog() {
    if (!dialog || !ring) return;
    syncSizeInputs('input[data-lc-dialog-size]');
    const submit = dialog.querySelector('[data-lc-dialog-submit]');
    if (!submit) return;
    if (dialogAction === 'set') {
      const state = setState();
      submit.textContent = state.complete ? fill(strings.set_add, 'price', buttonMoney(state.total)) : strings.set_choose;
    } else {
      submit.textContent = ring.variant
        ? fill(strings.add_with_price, 'price', buttonMoney(ring.displayPrice))
        : ring.data.choose_label;
    }
  }

  function openSizeDialog(action) {
    if (!dialog || typeof dialog.showModal !== 'function') {
      if (action === 'set') addSet();
      else if (ring) ring.add();
      return;
    }
    dialogAction = action;
    showError(dialog.querySelector('[data-lc-dialog-error]'), '');
    updateDialog();
    dialog.showModal();
    const checked = dialog.querySelector('input[data-lc-dialog-size]:checked') || dialog.querySelector('input[data-lc-dialog-size]');
    if (checked) checked.focus();
  }

  if (dialog) {
    dialog.addEventListener('change', (event) => {
      const input = event.target.closest('input[data-lc-dialog-size]');
      if (!input) return;
      showError(dialog.querySelector('[data-lc-dialog-error]'), '');
      setRingSize(input.value);
    });
    dialog.querySelector('[data-lc-dialog-form]').addEventListener('submit', (event) => {
      event.preventDefault();
      if (!ring || !ring.variant) {
        showError(dialog.querySelector('[data-lc-dialog-error]'), strings.choose_size_error);
        return;
      }
      dialog.close();
      if (dialogAction === 'set') addSet();
      else ring.add();
    });
    dialog.querySelector('[data-lc-dialog-close]').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
  }

  /* ---------- Sticky bar (mobile) ---------- */

  const sticky = root.querySelector('[data-lc-sticky]');
  const stickyState = { target: setKeys[0] || null, pastHero: false, pastEnd: false };

  function renderSticky() {
    if (!sticky || !stickyState.target) return;
    const title = sticky.querySelector('[data-lc-sticky-title]');
    const meta = sticky.querySelector('[data-lc-sticky-meta]');
    const price = sticky.querySelector('[data-lc-sticky-price]');
    const thumb = sticky.querySelector('[data-lc-sticky-thumb]');
    const button = sticky.querySelector('[data-lc-sticky-button]');

    if (stickyState.target === 'set') {
      const state = setState();
      title.textContent = strings.set_title;
      meta.textContent = state.enough
        ? fill(fill(strings.set_meta, 'count', state.count), 'percent', state.percent)
        : strings.set_min;
      price.textContent = formatMoney(state.total);
      const first = state.parts[0] || blocks.get(setKeys[0]);
      if (thumb && first) thumb.src = first.thumbnail;
      button.textContent = state.complete ? strings.sticky_add : strings.sticky_choose;
      button.disabled = !state.enough || (state.complete && !state.available);
      return;
    }

    const block = blocks.get(stickyState.target);
    if (!block) return;
    title.textContent = block.data.title;
    meta.textContent = block.summary();
    price.textContent = formatMoney(block.displayPrice);
    if (thumb) thumb.src = block.thumbnail;
    if (!block.variant) {
      button.textContent = strings.sticky_choose;
      button.disabled = false;
    } else {
      button.textContent = block.variant.available ? strings.sticky_add : strings.sold_out;
      button.disabled = !block.variant.available;
    }
  }

  function toggleSticky() {
    if (!sticky) return;
    const show = stickyState.pastHero && !stickyState.pastEnd && !!stickyState.target;
    sticky.classList.toggle('is-visible', show);
    sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
    sticky.inert = !show;
    document.documentElement.classList.toggle('lc-sticky-visible', show);
  }

  if (sticky && 'IntersectionObserver' in window) {
    const hero = root.querySelector('[data-lc-hero]');
    if (hero) {
      new IntersectionObserver(([entry]) => {
        stickyState.pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        toggleSticky();
      }).observe(hero);
    }

    const end = root.querySelector('[data-lc-end]');
    if (end) {
      new IntersectionObserver(([entry]) => {
        stickyState.pastEnd = entry.isIntersecting || entry.boundingClientRect.top < 0;
        toggleSticky();
      }).observe(end);
    }

    const ratios = new Map();
    const targetObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => ratios.set(entry.target.dataset.lcStickyTarget, entry.intersectionRatio));
        let best = null;
        let bestRatio = 0;
        ratios.forEach((ratio, key) => {
          if (ratio > bestRatio) {
            best = key;
            bestRatio = ratio;
          }
        });
        if (best) {
          stickyState.target = best;
          renderSticky();
        }
      },
      { threshold: [0, 0.15, 0.35, 0.6, 0.85, 1] }
    );
    root.querySelectorAll('[data-lc-sticky-target]').forEach((el) => targetObserver.observe(el));

    sticky.querySelector('[data-lc-sticky-button]').addEventListener('click', () => {
      if (stickyState.target === 'set') {
        const state = setState();
        const ringMissing = ring && isIncluded(ring.key) && !ring.variant;
        if (state.complete || !state.enough || !ringMissing) addSet();
        else openSizeDialog('set');
        return;
      }
      const block = blocks.get(stickyState.target);
      if (!block) return;
      if (block.variant) block.add();
      else if (block === ring) openSizeDialog('ring');
      else block.focusMissing();
    });
  }

  /* ---------- Wire up ---------- */

  root.addEventListener('lc:change', () => {
    syncGlobalMetal();
    updateSet();
    updateDialog();
    renderSticky();
  });

  syncGlobalMetal();
  updateSet();
  renderSticky();
  blocks.forEach((block) => block.syncThumbs());
})();